import { Form, InputNumber, TableProps } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiGetOutletOverdue } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { DETAIL, OUTLET } from '../../assets/constants/route_path'
import FormSearch from '../../components/form'
import CompanySelect from '../../components/outlet/CompanySelect'
import OutletSelect from '../../components/outlet/OutletSelect'
import ProvinceDistrictSelect from '../../components/outlet/ProvinceDistrictSelect'
import UserSelect from '../../components/outlet/UserSelect'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IOverdueRow } from '../../models/outlet.modal'
import { toastError } from '../../utils/toast'

const DEFAULT_OVERDUE_DAYS = 60

const formatNumber = (v?: number | null) => (v == null ? '—' : v.toLocaleString('vi-VN'))
const formatDate = (v?: string | null) => {
    if (!v) return '—'
    const d = dayjs(v)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY')
}

type SearchState = {
    overdueDays: number
    cpCodes?: string[]
    outletIds?: string[]
    ownerUserIds?: number[]
    provinces?: string[]
}

export default function OutletOverdue() {
    const [data, setData] = useState<IOverdueRow[]>([])
    const [total, setTotal] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<SearchState>({ overdueDays: DEFAULT_OVERDUE_DAYS })
    const [selectedProvinces, setSelectedProvinces] = useState<string[]>([])
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp: any = await apiGetOutletOverdue({
                overdueDays: search.overdueDays,
                cpCodes: search.cpCodes,
                outletIds: search.outletIds,
                ownerUserIds: search.ownerUserIds,
                provinces: search.provinces,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
        } catch (error) {
            console.log(error)
            toastError('Không tải được cảnh báo tồn hạn')
            setData([])
            setTotal(0)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        setSearch({
            overdueDays: Number(values.overdueDays) || DEFAULT_OVERDUE_DAYS,
            cpCodes: values.cpCodes,
            outletIds: values.outletIds,
            ownerUserIds: values.ownerUserIds,
            provinces: selectedProvinces.length ? selectedProvinces : undefined,
        })
        setCurrentPage(defaultParams.PAGE)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSelectedProvinces([])
        setSearch({ overdueDays: DEFAULT_OVERDUE_DAYS })
        setCurrentPage(defaultParams.PAGE)
    }

    const columns: TableProps<IOverdueRow>['columns'] = [
        {
            key: 'outletName',
            title: 'Đại lý',
            fixed: 'left',
            render: (_, r) => <Link to={OUTLET + DETAIL + `?id=${r.outletId}`}>{r.outletName || '(không tên)'}</Link>,
        },
        {
            key: 'region',
            title: 'Khu vực',
            render: (_, r) => [r.province, r.district].filter(Boolean).join(' / ') || '—',
        },
        {
            key: 'ownerUserName',
            title: 'Sale',
            render: (_, r) => r.ownerUserName || (r.ownerUserId ? `#${r.ownerUserId}` : '—'),
        },
        {
            key: 'simRange',
            title: 'SIM',
            render: (_, r) => `${r.startSim || '—'} .. ${r.endSim || '—'}`,
        },
        {
            key: 'totalSim',
            title: 'Tổng SIM',
            align: 'right',
            render: (_, r) => formatNumber(r.totalSim),
        },
        {
            key: 'totalActive',
            title: 'Đã đấu',
            align: 'right',
            render: (_, r) => formatNumber(r.totalActive),
        },
        {
            key: 'remainingSim',
            title: 'Còn lại',
            align: 'right',
            render: (_, r) => formatNumber(r.remainingSim),
        },
        {
            key: 'linkedAt',
            title: 'Ngày giao',
            render: (_, r) => formatDate(r.linkedAt),
        },
        {
            key: 'daysHeld',
            title: 'Tồn (ngày)',
            align: 'right',
            render: (_, r) => (
                <span style={{ color: '#ec4969', fontWeight: 700 }}>{formatNumber(r.daysHeld)}</span>
            ),
        },
    ]

    return (
        <div>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='overdueDays' label='Ngưỡng tồn (ngày)' initialValue={DEFAULT_OVERDUE_DAYS} style={{ minWidth: 180 }}>
                    <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name='cpCodes' label='Công ty' style={{ minWidth: 240 }}>
                    <CompanySelect multiple onlyWithOutlets placeholder='Chọn công ty' />
                </Form.Item>
                <Form.Item name='outletIds' label='Đại lý' style={{ minWidth: 240 }}>
                    <OutletSelect multiple placeholder='Chọn đại lý' />
                </Form.Item>
                <Form.Item label='Tỉnh/TP'>
                    <ProvinceDistrictSelect
                        provinces={selectedProvinces}
                        onProvincesChange={setSelectedProvinces}
                        showDistrict={false}
                    />
                </Form.Item>
                <Form.Item name='ownerUserIds' label='Sale' style={{ minWidth: 240 }}>
                    <UserSelect multiple placeholder='Chọn sale' />
                </Form.Item>
            </FormSearch>

            <CustomTable
                namePage='SIM tồn'
                rowKey={(r: IOverdueRow) => `${r.outletId}_${r.simInventoryId}`}
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
        </div>
    )
}
